/**
 * Auth Store - Laumeris V2
 * Pinia store for authentication state
 */

import { defineStore } from 'pinia'
import { ref, computed } from 'vue'

const API_BASE_URL = import.meta.env.VITE_API_URL || 'https://api.laumeris.com/api'

export interface User {
  id: number
  email: string
  firstName: string
  lastName: string
  userType: 'owner' | 'admin' | 'comptable' | 'client'
  adminRole?: 'salarie' | 'responsable' | 'directeur'
  avatar?: string
  phone?: string
  companyName?: string
  lastLogin?: string
  lastPasswordChange?: string
  profileCompleted?: boolean
  tutorialCompleted?: boolean
  needsProfileCompletion?: boolean
  mustChangePassword?: boolean
  emailVerified?: boolean
  createdAt: string
  permissions?: string[]
  isSuperAdmin?: boolean
}

export interface LoginCredentials {
  email: string
  password: string
  rememberMe?: boolean
}

export interface LoginResult {
  success: boolean
  error?: string
  requires2FA?: boolean
  tempToken?: string
  twoFactorMethod?: 'totp' | 'email'
}

export interface Verify2FAParams {
  tempToken: string
  code: string
  method?: 'totp' | 'email' | 'backup_code'
  trustDevice?: boolean
}

export const useAuthStore = defineStore('auth', () => {
  // State
  const user = ref<User | null>(null)
  const accessToken = ref<string | null>(null)
  const refreshToken = ref<string | null>(null)
  const isLoading = ref(false)
  const isInitialized = ref(false)

  // Getters
  const isAuthenticated = computed(() => !!user.value && !!accessToken.value)
  const isOwner = computed(() => user.value?.userType === 'owner')
  const isAdmin = computed(() => user.value?.userType === 'admin')
  const isComptable = computed(() => user.value?.userType === 'comptable')
  const isClient = computed(() => user.value?.userType === 'client')

  const fullName = computed(() => {
    if (!user.value) return ''
    return `${user.value.firstName} ${user.value.lastName}`
  })

  const userInitials = computed(() => {
    if (!user.value) return ''
    return `${user.value.firstName[0]}${user.value.lastName[0]}`.toUpperCase()
  })

  // Actions
  const setUser = (newUser: User | null) => {
    user.value = newUser
  }

  const setTokens = (access: string, refresh: string) => {
    accessToken.value = access
    refreshToken.value = refresh
    localStorage.setItem('accessToken', access)
    localStorage.setItem('refreshToken', refresh)
  }

  const clearAuth = () => {
    user.value = null
    accessToken.value = null
    refreshToken.value = null
    localStorage.removeItem('accessToken')
    localStorage.removeItem('refreshToken')
  }

  const initialize = () => {
    if (isInitialized.value) return

    const storedAccess = localStorage.getItem('accessToken')
    const storedRefresh = localStorage.getItem('refreshToken')

    if (storedAccess && storedRefresh) {
      accessToken.value = storedAccess
      refreshToken.value = storedRefresh
    }

    isInitialized.value = true
  }

  // Computed pour le Super Admin
  const isSuperAdmin = computed(() => user.value?.isSuperAdmin === true || user.value?.id === 1)

  /**
   * Vérifie si l'utilisateur a une permission spécifique
   * Basé sur les vraies permissions chargées depuis la base de données
   */
  const hasPermission = (permission: string): boolean => {
    if (!user.value) return false

    // Super Admin a TOUTES les permissions
    if (user.value.isSuperAdmin || user.value.id === 1) {
      return true
    }

    // Owner a toutes les permissions
    if (user.value.userType === 'owner') {
      return true
    }

    // Vérifier les permissions chargées depuis l'API
    const userPerms = user.value.permissions || []
    
    // Check for wildcard permission
    if (userPerms.includes('admin:*') || userPerms.includes('super_admin')) {
      return true
    }
    
    // Check exact permission
    if (userPerms.includes(permission)) {
      return true
    }
    
    // Check category wildcard (e.g., "clients:*" matches "clients:read")
    const [category] = permission.split(':')
    if (userPerms.includes(`${category}:*`)) {
      return true
    }

    return false
  }

  /**
   * Vérifie si l'utilisateur a au moins une des permissions
   */
  const hasAnyPermission = (...permissions: string[]): boolean => {
    return permissions.some(p => hasPermission(p))
  }

  /**
   * Vérifie si l'utilisateur a toutes les permissions
   */
  const hasAllPermissions = (...permissions: string[]): boolean => {
    return permissions.every(p => hasPermission(p))
  }

  const hasRole = (...roles: string[]): boolean => {
    if (!user.value) return false
    return roles.includes(user.value.userType)
  }

  /**
   * Login avec email/password
   */
  const login = async (credentials: LoginCredentials): Promise<LoginResult> => {
    isLoading.value = true
    
    try {
      const response = await fetch(`${API_BASE_URL}/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(credentials)
      })
      
      const data = await response.json()
      
      if (!response.ok) {
        return { success: false, error: data.message || 'Erreur de connexion' }
      }
      
      // 2FA requis
      if (data.requires2FA) {
        return {
          success: true,
          requires2FA: true,
          tempToken: data.tempToken,
          twoFactorMethod: data.twoFactorMethod
        }
      }
      
      // Connexion réussie
      if (data.accessToken && data.refreshToken) {
        setTokens(data.accessToken, data.refreshToken)
        if (data.user) {
          setUser(data.user)
        } else {
          await fetchUser()
        }
        return { success: true }
      }
      
      return { success: false, error: 'Réponse inattendue du serveur' }
    } catch (error) {
      return { success: false, error: 'Erreur de connexion au serveur' }
    } finally {
      isLoading.value = false
    }
  }

  /**
   * Vérification 2FA
   */
  const verify2FA = async (params: Verify2FAParams): Promise<LoginResult> => {
    isLoading.value = true
    
    try {
      const response = await fetch(`${API_BASE_URL}/security/2fa/verify`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(params)
      })
      
      const data = await response.json()
      
      if (!response.ok) {
        return { success: false, error: data.message || 'Code invalide' }
      }
      
      if (data.accessToken && data.refreshToken) {
        setTokens(data.accessToken, data.refreshToken)
        if (data.user) {
          setUser(data.user)
        } else {
          await fetchUser()
        }
        return { success: true }
      }
      
      return { success: false, error: 'Réponse inattendue du serveur' }
    } catch (error) {
      return { success: false, error: 'Erreur de connexion au serveur' }
    } finally {
      isLoading.value = false
    }
  }

  /**
   * Déconnexion
   */
  const logout = async (): Promise<void> => {
    try {
      if (accessToken.value) {
        await fetch(`${API_BASE_URL}/auth/logout`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${accessToken.value}`
          }
        })
      }
    } catch (error) {
      console.error('Erreur lors de la déconnexion:', error)
    } finally {
      clearAuth()
    }
  }

  /**
   * Récupérer le profil utilisateur
   */
  const fetchUser = async (): Promise<boolean> => {
    if (!accessToken.value) return false
    
    isLoading.value = true
    
    try {
      const response = await fetch(`${API_BASE_URL}/auth/me`, {
        headers: {
          'Authorization': `Bearer ${accessToken.value}`
        }
      })
      
      if (!response.ok) {
        if (response.status === 401) {
          clearAuth()
        }
        return false
      }
      
      const data = await response.json()
      if (data.user) {
        setUser(data.user)
        return true
      }
      
      return false
    } catch (error) {
      console.error('Erreur lors de la récupération du profil:', error)
      return false
    } finally {
      isLoading.value = false
    }
  }

  /**
   * Initialiser le store et vérifier l'authentification
   */
  const initializeAuth = async () => {
    if (isInitialized.value) return
    
    initialize()
    
    if (accessToken.value) {
      await fetchUser()
    }
  }

  /**
   * Marquer le tutoriel comme terminé (côté serveur)
   */
  const markTutorialCompleted = async (): Promise<boolean> => {
    if (!accessToken.value) return false

    try {
      const response = await fetch(`${API_BASE_URL}/auth/tutorial-completed`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${accessToken.value}`,
          'Content-Type': 'application/json'
        }
      })

      if (response.ok) {
        // Update local user state
        if (user.value) {
          user.value.tutorialCompleted = true
        }
        return true
      }
      return false
    } catch (error) {
      console.error('Error marking tutorial as completed:', error)
      return false
    }
  }

  // Computed for onboarding state
  const needsProfileCompletion = computed(() => user.value?.needsProfileCompletion ?? false)
  const needsTutorial = computed(() => user.value?.tutorialCompleted === false)

  return {
    // State
    user,
    accessToken,
    refreshToken,
    isLoading,
    isInitialized,

    // Getters
    isAuthenticated,
    isOwner,
    isAdmin,
    isComptable,
    isClient,
    isSuperAdmin,
    fullName,
    userInitials,
    needsProfileCompletion,
    needsTutorial,

    // Actions
    setUser,
    setTokens,
    clearAuth,
    initialize,
    initializeAuth,
    hasPermission,
    hasAnyPermission,
    hasAllPermissions,
    hasRole,
    login,
    logout,
    verify2FA,
    fetchUser,
    markTutorialCompleted
  }
})
